import PropTypes from 'prop-types'
import { Row, Col } from 'react-bootstrap'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import {
  DateInfoRow,
  DateBox,
  DateBoxMonth,
  DateBoxDay,
  TimeInfoWrapper,
  TimeInfoRow,
  MutedTimeInfoRow,
  ProposedBadge,
  ProposedDot,
  SingleSlotHint,
  ActionsContainer,
  ActionButton,
  CardBody,
} from './styled'
import theme from '../../constants/theme'
import TimeCircle from '../../assets/svgs/timeCircle'
import Monitor from '../../assets/svgs/Monitor'
import { months } from '../../constants/months'

/**
 * The one time the interviewer put forward, rendered as a fixed summary.
 *
 * When only one slot is offered there is nothing to pick, so the candidate sees
 * the proposed date and time instead of the slot picker.
 */
function ProposedSlotRow({ slot, locale, interviewType }) {
  const { t } = useTranslation()
  const start = dayjs(slot.start)
  const end = slot.end ? dayjs(slot.end) : null
  const monthEntry = months[start.month()]
  const monthLabel = monthEntry?.[locale] || monthEntry?.en
  const timeRange = end
    ? `${start.format('hh:mm A')} - ${end.format('hh:mm A')}`
    : start.format('hh:mm A')
  const typeLabel =
    interviewType?.toLowerCase() === 'inperson'
      ? t('calendar.inPersonInterview')
      : t('calendar.scheduledInterview')

  return (
    <DateInfoRow>
      <DateBox>
        <DateBoxMonth>{monthLabel}</DateBoxMonth>
        <DateBoxDay>{start.date()}</DateBoxDay>
      </DateBox>
      <TimeInfoWrapper>
        <TimeInfoRow>
          <TimeCircle width="16" height="16" />
          <span>
            {start.format('dddd, D MMMM')} &middot; {timeRange}
          </span>
          <ProposedBadge>
            <ProposedDot />
            {t('calendar.proposedTime')}
          </ProposedBadge>
        </TimeInfoRow>
        <MutedTimeInfoRow>
          <Monitor width={16} height={16} color="#6B7280" />
          <span>{typeLabel}</span>
        </MutedTimeInfoRow>
      </TimeInfoWrapper>
    </DateInfoRow>
  )
}

ProposedSlotRow.propTypes = {
  slot: PropTypes.shape({
    start: PropTypes.string.isRequired,
    end: PropTypes.string,
  }).isRequired,
  locale: PropTypes.string,
  interviewType: PropTypes.string,
}

ProposedSlotRow.defaultProps = {
  locale: 'en',
  interviewType: '',
}

/**
 * Decline / approve for a single offered slot.
 *
 * Approving books that slot outright, so it carries the same actions as the
 * picker's submit button without a date to choose first.
 */
function SingleSlotActions({ isSubmitting, onApprove, onDecline }) {
  const { t } = useTranslation()

  return (
    <CardBody>
      <SingleSlotHint>{t('calendar.singleSlotHint')}</SingleSlotHint>
      <ActionsContainer as={Row}>
        <Col md={6} sm={12} className="p-1">
          <ActionButton
            width="100%"
            backgroundcolor="transparent"
            border={`1px solid ${theme.talentinoRed}`}
            hovercolor={theme.talentinoRed}
            color={theme.talentinoRed}
            onClick={onDecline}
            disabled={isSubmitting}
          >
            {t('calendar.declineInvitation')}
          </ActionButton>
        </Col>
        <Col md={6} sm={12} className="p-1">
          <ActionButton
            width="100%"
            onClick={onApprove}
            disabled={isSubmitting}
          >
            {t('calendar.approveInvitation')}
          </ActionButton>
        </Col>
      </ActionsContainer>
    </CardBody>
  )
}

SingleSlotActions.propTypes = {
  isSubmitting: PropTypes.bool,
  onApprove: PropTypes.func.isRequired,
  onDecline: PropTypes.func.isRequired,
}

SingleSlotActions.defaultProps = {
  isSubmitting: false,
}

export { ProposedSlotRow, SingleSlotActions }
